/**
 * Regenerative Concepts Interactive Section
 * Handles hover interactions and content switching for the regenerative agriculture concepts
 */

class RegenerativeConcepts {
  constructor() {
    this.conceptCircles = document.querySelectorAll('.concept-circle');
    this.conceptDetails = document.querySelectorAll('.concept-detail');
    this.activeConcept = null;
    this.hoverTimeout = null;
    this.clickedConcept = null; // Track clicked state separately from hover
    this.isDesktop = window.innerWidth > 768; // Detect desktop vs mobile
    
    this.init();
  }

  init() {
    if (this.conceptCircles.length === 0) return;
    
    this.bindEvents();
    this.setInitialState();
  }

  bindEvents() {
    this.conceptCircles.forEach(circle => {
      // Desktop: Both hover and click functionality
      if (this.isDesktop) {
        circle.addEventListener('mouseenter', (e) => this.handleMouseEnter(e));
        circle.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
      }
      
      // Click functionality for both desktop and mobile
      circle.addEventListener('click', (e) => this.handleClick(e));
      
      // Add keyboard navigation support
      circle.addEventListener('keydown', (e) => this.handleKeydown(e));
      circle.setAttribute('tabindex', '0');
      circle.setAttribute('role', 'button');
      circle.setAttribute('aria-label', `Learn about ${circle.querySelector('.concept-label').textContent}`);
    });

    // Handle window resize to update desktop/mobile detection
    window.addEventListener('resize', () => {
      this.isDesktop = window.innerWidth > 768;
    });
  }

  handleMouseEnter(event) {
    clearTimeout(this.hoverTimeout);
    const conceptKey = event.currentTarget.getAttribute('data-concept');
    
    // If hovering over the clicked concept, remove the click lock
    if (this.clickedConcept === conceptKey) {
      this.clickedConcept = null;
    }
    
    this.activateConcept(conceptKey);
  }

  handleMouseLeave(event) {
    // Add a small delay to prevent flickering when moving between circles
    this.hoverTimeout = setTimeout(() => {
      if (!this.activeConcept) {
        this.setInitialState();
      }
    }, 100);
  }

  handleClick(event) {
    event.preventDefault();
    const conceptKey = event.currentTarget.getAttribute('data-concept');
    
    // If clicking the same concept that's already clicked, toggle it off
    if (this.clickedConcept === conceptKey) {
      this.clickedConcept = null;
      this.activeConcept = null;
      this.setInitialState();
    } else {
      // Set clicked concept and activate it
      this.clickedConcept = conceptKey;
      this.activateConcept(conceptKey);
      this.activeConcept = conceptKey;
    }
    
    // Clear any pending hover timeout
    clearTimeout(this.hoverTimeout);
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick(event);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateConcepts(event.key === 'ArrowRight' ? 1 : -1);
    }
  }

  navigateConcepts(direction) {
    const currentIndex = Array.from(this.conceptCircles).findIndex(
      circle => circle.getAttribute('data-active') === 'true'
    );
    
    let newIndex = currentIndex + direction;
    
    if (newIndex < 0) {
      newIndex = this.conceptCircles.length - 1;
    } else if (newIndex >= this.conceptCircles.length) {
      newIndex = 0;
    }
    
    const newConceptKey = this.conceptCircles[newIndex].getAttribute('data-concept');
    this.activateConcept(newConceptKey);
    this.activeConcept = newConceptKey;
    
    // Focus the new circle
    this.conceptCircles[newIndex].focus();
  }

  activateConcept(conceptKey) {
    // Remove active state from all elements
    this.conceptCircles.forEach(circle => {
      circle.setAttribute('data-active', 'false');
      circle.setAttribute('data-clicked', 'false');
    });
    
    this.conceptDetails.forEach(detail => {
      detail.setAttribute('data-active', 'false');
    });

    // Add active state to current concept
    const activeCircle = document.querySelector(`.concept-circle[data-concept="${conceptKey}"]`);
    const activeDetail = document.querySelector(`.concept-detail[data-concept="${conceptKey}"]`);
    
    if (activeCircle) {
      activeCircle.setAttribute('data-active', 'true');
      
      // Mark as clicked if this is a clicked concept
      if (this.clickedConcept === conceptKey) {
        activeCircle.setAttribute('data-clicked', 'true');
      }
    }
    
    if (activeDetail) {
      activeDetail.setAttribute('data-active', 'true');
    }

    // Add smooth transition effect
    this.addTransitionEffect(activeDetail);
  }

  addTransitionEffect(element) {
    if (!element) return;
    
    element.style.opacity = '0';
    element.style.transform = 'translateY(10px)';
    
    requestAnimationFrame(() => {
      element.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    });
  }

  setInitialState() {
    if (this.conceptCircles.length > 0) {
      const firstConceptKey = this.conceptCircles[0].getAttribute('data-concept');
      this.activateConcept(firstConceptKey);
    }
  }

  // Public method to programmatically activate a concept
  showConcept(conceptKey) {
    this.activateConcept(conceptKey);
    this.activeConcept = conceptKey;
  }

  // Public method to reset to initial state
  reset() {
    this.activeConcept = null;
    this.clickedConcept = null;
    this.setInitialState();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Check if we're on a page with the regenerative concepts section
  if (document.querySelector('.regenerative-concepts-section')) {
    window.regenerativeConcepts = new RegenerativeConcepts();
  }
});

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RegenerativeConcepts;
}

